import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip,
    ResponsiveContainer, CartesianGrid
} from "recharts";
import AdminLayout, { AdminPageHead } from "./AdminLayout";
import { request } from "../service/HttpService";
import { isRateLimited } from "../service/HttpErrors";
import { getJwtRole } from "../service/AuthService";
import { admin } from "../constants/Constants";

const ACTION_LABELS = {
    UPLOAD_MANUSCRIPT: "Upload",
    VIEW_SUBMISSION: "View Submission",
    VIEW_GRAPH: "View Graph",
    VIEW_SIMILAR_WORKS: "Similar Works",
    VIEW_DEEPTHINKERS: "Deep Thinkers",
    DOWNLOAD_REPORT: "Download Report",
    SHARE_REPORT: "Share Report",
    CERTIFY_MANUSCRIPT: "Certify",
    DELETE_MANUSCRIPT: "Delete",
    SUBMIT_PEER_REVIEW: "Submit Peer Review",
    CANCEL_PEER_REVIEW: "Cancel Peer Review",
    ACCEPT_REVIEW_ASSIGNMENT: "Accept Review",
    DECLINE_REVIEW_ASSIGNMENT: "Decline Review",
    SUBMIT_REVIEW_FEEDBACK: "Review Feedback",
    PAGE_VIEW: "Page View",
};

// Fallback for action types added after this map — "SOME_NEW_ACTION" → "Some New Action"
function actionLabel(action) {
    return ACTION_LABELS[action] ?? action
        .split("_")
        .map(w => w.charAt(0) + w.slice(1).toLowerCase())
        .join(" ");
}

const PAGE_LABELS = {
    "/": "Home",
    "/upload": "Upload",
    "/submissions": "Submissions",
    "/submission": "Submission Detail",
    "/network": "Network Graph",
    "/similar-works": "Similar Works",
    "/deepthinkers": "Deep Thinkers",
    "/analytics": "My Analytics",
    "/peer-reviews": "Peer Reviews",
    "/reviewer": "Reviewer Dashboard",
    "/settings": "Settings",
    "/profile": "Profiles",
    "/showcase": "Showcase",
    "/onboard": "Onboarding",
    "/faq": "FAQ",
    "/about": "About",
    "/feedback": "Feedback",
    "/privacy": "Privacy",
    "/terms": "Terms",
    "/report/verify": "Verify Report",
    "/tutorial": "Tutorial",
    "/funders": "For Funders",
    "/funder": "Funder: Overview",
    "/funder/submissions": "Funder: Submissions",
    "/funder/proposal": "Funder: Proposal",
    "/funder/reviewer-management": "Funder: Reviewers",
    "/funder/committee": "Funder: Committee",
    "/funder/decisions": "Funder: Decisions",
    "/funder/portfolio": "Funder: Portfolio",
    "/funder/comparison": "Funder: Comparison",
    "/funder/preprints": "Funder: Preprints",
    "/funder/white-space": "Funder: White Space",
};

// Bars get cramped at a fixed height once the type list grows
function barChartHeight(rows) {
    return Math.max(240, rows * 26 + 30);
}

function formatDate(dateStr) {
    const d = new Date(dateStr + "T00:00:00");
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function formatTimestamp(ts) {
    if (!ts) return "";
    return new Date(ts).toLocaleString("en-US", {
        month: "short", day: "numeric",
        hour: "numeric", minute: "2-digit", hour12: true,
    });
}

function ChartCard({ title, subtitle, children }) {
    return (
        <div className="submissions-card" style={{ padding: "20px" }}>
            <p style={{ margin: 0, fontSize: 14.5, fontWeight: 600, color: "var(--ink-900)" }}>{title}</p>
            {subtitle && <p style={{ margin: 0, fontSize: 12, color: "var(--ink-300)", marginTop: 2 }}>{subtitle}</p>}
            <div style={{ marginTop: 16 }}>{children}</div>
        </div>
    );
}

export default function UserActivityPage() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);
    const [data, setData] = useState(null);

    useEffect(() => {
        if (getJwtRole() !== admin) { navigate("/"); return; }
        request("GET", "/admin/activity")
            .then(res => { setData(res); setLoading(false); })
            .catch(err => isRateLimited(err) ? setLoadError(err.message) : navigate("/login"));
    }, []);

    if (loading) {
        return (
            <AdminLayout>
                        <div style={{ padding: "60px 20px", textAlign: "center", color: "var(--ink-300)", fontSize: 13 }}>{loadError || "Loading activity..."}</div>
            </AdminLayout>
        );
    }

    const featureUsage = (data.featureUsage ?? [])
        .filter(f => f.action !== "VIEW_SUBMISSION")
        .map(f => ({
            name: actionLabel(f.action),
            count: f.count,
        }));

    const pageViews = (data.pageViews ?? []).map(p => ({
        name: PAGE_LABELS[p.page] ?? p.page,
        count: p.count,
    }));

    // Backfill 30 days so the chart always has a continuous range,
    // even when the cloud DB only has a couple days of data.
    const activityByDay = (() => {
        const countsByDate = new Map((data.activityByDay ?? []).map(d => [d.date, d.count]));
        const days = [];
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        for (let i = 29; i >= 0; i--) {
            const d = new Date(today);
            d.setDate(d.getDate() - i);
            const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
            days.push({
                date: iso,
                count: countsByDate.get(iso) ?? 0,
                label: formatDate(iso),
            });
        }
        return days;
    })();

    const mostActive = data.mostActiveUsers ?? [];
    const recent = data.recentActivity ?? [];

    return (
        <AdminLayout>

                    <AdminPageHead
                        title="User Activity"
                        subtitle="What users are doing across the platform"
                    />

                    {/* Charts row: Feature usage + Page views */}
                    <div className="analytics-charts-row">
                        <ChartCard title="Feature Usage" subtitle="Total actions by type">
                            {featureUsage.length === 0 ? (
                                <p style={{ color: "var(--ink-300)", fontSize: 13, textAlign: "center", padding: 20 }}>No data yet</p>
                            ) : (
                                <ResponsiveContainer width="100%" height={barChartHeight(featureUsage.length)}>
                                    <BarChart data={featureUsage} layout="vertical" margin={{ top: 4, right: 12, left: 10, bottom: 0 }}>
                                        <CartesianGrid stroke="var(--border-subtle)" strokeDasharray="4 4" horizontal={false} />
                                        <XAxis type="number" allowDecimals={false} tick={{ fontSize: 10, fill: "var(--ink-300)" }} axisLine={false} tickLine={false} />
                                        <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: "var(--ink-500)" }} axisLine={false} tickLine={false} width={120} />
                                        <Tooltip contentStyle={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 8, fontSize: 12 }} />
                                        <Bar dataKey="count" fill="var(--accent)" radius={[0, 4, 4, 0]} name="Actions" />
                                    </BarChart>
                                </ResponsiveContainer>
                            )}
                        </ChartCard>

                        <ChartCard title="Page Views" subtitle="Visits by page (includes logged-out visitors)">
                            {pageViews.length === 0 ? (
                                <p style={{ color: "var(--ink-300)", fontSize: 13, textAlign: "center", padding: 20 }}>No data yet</p>
                            ) : (
                                <ResponsiveContainer width="100%" height={barChartHeight(pageViews.length)}>
                                    <BarChart data={pageViews} layout="vertical" margin={{ top: 4, right: 12, left: 10, bottom: 0 }}>
                                        <CartesianGrid stroke="var(--border-subtle)" strokeDasharray="4 4" horizontal={false} />
                                        <XAxis type="number" allowDecimals={false} tick={{ fontSize: 10, fill: "var(--ink-300)" }} axisLine={false} tickLine={false} />
                                        <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: "var(--ink-500)" }} axisLine={false} tickLine={false} width={120} />
                                        <Tooltip contentStyle={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 8, fontSize: 12 }} />
                                        <Bar dataKey="count" fill="var(--accent)" radius={[0, 4, 4, 0]} name="Views" />
                                    </BarChart>
                                </ResponsiveContainer>
                            )}
                        </ChartCard>
                    </div>

                    {/* Middle row: Activity over time + Most active users */}
                    <div className="analytics-charts-row">
                        <ChartCard title="Activity Over Time" subtitle="Actions per day (last 30 days)">
                            {activityByDay.length === 0 ? (
                                <p style={{ color: "var(--ink-300)", fontSize: 13, textAlign: "center", padding: 20 }}>No data yet</p>
                            ) : (
                                <ResponsiveContainer width="100%" height={240}>
                                    <LineChart data={activityByDay} margin={{ top: 4, right: 12, left: -20, bottom: 0 }}>
                                        <CartesianGrid stroke="var(--border-subtle)" strokeDasharray="4 4" vertical={false} />
                                        <XAxis dataKey="label" tick={{ fontSize: 10, fill: "var(--ink-300)" }} axisLine={false} tickLine={false} />
                                        <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "var(--ink-300)" }} axisLine={false} tickLine={false} />
                                        <Tooltip contentStyle={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 8, fontSize: 12 }} />
                                        <Line type="monotone" dataKey="count" stroke="var(--blue)" strokeWidth={2} dot={false} name="Actions" />
                                    </LineChart>
                                </ResponsiveContainer>
                            )}
                        </ChartCard>

                        <ChartCard title="Most Active Users" subtitle="Top 10 by total actions">
                            {mostActive.length === 0 ? (
                                <p style={{ color: "var(--ink-300)", fontSize: 13, textAlign: "center", padding: 20 }}>No data yet</p>
                            ) : (
                                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                                    {mostActive.map((u, i) => (
                                        <div key={i} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                            <span style={{
                                                width: 24, height: 24, borderRadius: "50%", background: "var(--blue-bg)",
                                                color: "var(--blue)", display: "flex", alignItems: "center", justifyContent: "center",
                                                fontSize: 11, fontWeight: 600, flexShrink: 0
                                            }}>{i + 1}</span>
                                            <span style={{ flex: 1, fontSize: 13, color: "var(--ink-900)", fontWeight: 500 }}>{u.name}</span>
                                            <span style={{
                                                fontSize: 12, color: "var(--ink-500)", background: "var(--surface-3)",
                                                padding: "2px 10px", borderRadius: 6, fontWeight: 500
                                            }}>{u.count}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </ChartCard>
                    </div>

                    {/* Bottom: Recent activity (full width) */}
                    <div style={{ marginBottom: 16 }}>
                        <ChartCard title="Recent Activity" subtitle="Last 50 actions">
                            {recent.length === 0 ? (
                                <p style={{ color: "var(--ink-300)", fontSize: 13, textAlign: "center", padding: 20 }}>No data yet</p>
                            ) : (
                                <div style={{ maxHeight: 320, overflowY: "auto", overflowX: "auto" }}>
                                    <table className="submissions-table" style={{ fontSize: 12 }}>
                                        <thead>
                                            <tr>
                                                <th>User</th>
                                                <th>Action</th>
                                                <th>When</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {recent.map((r, i) => (
                                                <tr key={i}>
                                                    <td style={{ fontWeight: 500 }}>{r.userName}</td>
                                                    <td>
                                                        <span style={{
                                                            fontSize: 11, padding: "2px 8px", borderRadius: 5,
                                                            background: "var(--blue-bg)", color: "var(--blue)", fontWeight: 500,
                                                        }}>
                                                            {actionLabel(r.action)}
                                                        </span>
                                                    </td>
                                                    <td style={{ color: "var(--ink-350)", whiteSpace: "nowrap" }}>{formatTimestamp(r.timestamp)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </ChartCard>
                    </div>

        </AdminLayout>
    );
}
